import { isRejectedWithValue } from '@reduxjs/toolkit';

import { ExtraReducerBuilder } from '../../types/shared';
import { WritableQuoteBuilderStateType } from '../../types/state';
import { buildQuotePriceRejectedIssueId } from '../actions/quote-price-created';
import { isFormDirty } from '../selectors/quote-builder-dirty';
import { createQuotePriceOptimistically } from '../thunks/create-quote-price-optimistically';
import { upsertIssue } from '../utilities/issue-managers';
import { stringifyQuote } from '../utilities/stringify-quote';

type CreateQuotePriceOptimisticallyFulfilledAction = ReturnType<
  typeof createQuotePriceOptimistically.fulfilled
>;

type CreateQuotePriceOptimisticallyRejectedAction = ReturnType<
  typeof createQuotePriceOptimistically.rejected
>;

function quotePriceCreationSucceededBase(
  state: WritableQuoteBuilderStateType,
  action: CreateQuotePriceOptimisticallyFulfilledAction,
) {
  const wasFormDirty = isFormDirty(state);

  const { quoteLineUuid, quotePriceUuid } = action.meta;

  const quoteLine = state.quoteLines[quoteLineUuid];
  const quotePrice = quoteLine?.quotePrices[quotePriceUuid];

  if (!quotePrice) {
    return;
  }

  quotePrice.status = 'ready';

  const rejectedIssueId = buildQuotePriceRejectedIssueId({
    quoteLineUuid,
    quotePriceUuid,
  });

  delete state.issues[rejectedIssueId];

  state.pendingServerData = null;

  if (!wasFormDirty) {
    state.lastSaveDateTime = new Date();
    state.currentServerData = stringifyQuote(state);
  }
}

function quotePriceCreationFailed(
  state: WritableQuoteBuilderStateType,
  action: CreateQuotePriceOptimisticallyRejectedAction,
) {
  if (!action.meta.quotePriceUuid) {
    return;
  }

  const { quoteLineUuid, quotePriceUuid } = action.meta;

  const quoteLine = state.quoteLines[quoteLineUuid];
  const quotePrice = quoteLine?.quotePrices[quotePriceUuid];

  if (!quotePrice) {
    return;
  }

  quotePrice.status = 'creation-failed';

  upsertIssue(
    state,
    buildQuotePriceRejectedIssueId({
      quoteLineUuid,
      quotePriceUuid,
    }),
    {
      title: 'Failed to Create Price',
      description: 'Quote price could not be created in the database',
      area: 'quotePrice',
      quoteLineUuid,
      quotePriceUuid,
      severity: 'error',
      type: 'rejected',
      action: {
        type: 'unrecoverable',
      },
    },
  );

  state.pendingServerData = null;
}

export function createSnapQuotePriceReducer(builder: ExtraReducerBuilder) {
  builder.addMatcher(
    createQuotePriceOptimistically.fulfilled.match,
    quotePriceCreationSucceededBase,
  );

  builder.addMatcher(
    isRejectedWithValue(createQuotePriceOptimistically),
    quotePriceCreationFailed,
  );
}
